import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { ActionRecord, UpdateActionRequest } from "../ActionRecord";

interface ActionOptions {
  throwOnError: boolean;
  resourceOwnerId: string;
}

export function useAction(
  name: string | undefined,
  digest: string | undefined,
  options?: Partial<ActionOptions>,
) {
  const { actionService } = useDomainServices();

  return useQuery<ActionRecord, Error>({
    queryKey: ["actionRecord", name],
    queryFn({ signal }) {
      if (name === undefined) {
        return Promise.reject(new Error("Invalid action name"));
      }

      return actionService.getAction(name, digest, {
        abortSignal: signal,
        resourceOwnerId: options?.resourceOwnerId,
      });
    },
    enabled: name !== undefined,
    // 1 Minute until stale
    staleTime: 60000,
    throwOnError: options?.throwOnError ?? false,
  });
}

export interface UpdateAction {
  name: string;
  resourceOwnerId: string;
  updates: UpdateActionRequest;
}

export function useUpdateAction() {
  const { actionService } = useDomainServices();

  const queryClient = useQueryClient();

  return useMutation<ActionRecord, Error, UpdateAction>({
    mutationFn: ({ name, resourceOwnerId, updates }) => {
      return actionService.updateAction(name, updates, {
        resourceOwnerId,
      });
    },

    onSuccess: (actionRecord) => {
      queryClient.setQueryData(
        ["actionRecord", actionRecord.name],
        actionRecord,
      );
    },
  });
}

interface DeleteActionParams {
  name: ActionRecord["name"];
  resourceOwnerId: string;
}

export function useDeleteAction() {
  const { actionService } = useDomainServices();

  return useMutation<void, Error, DeleteActionParams>({
    mutationFn: ({ name, resourceOwnerId }) => {
      return actionService.deleteAction(name, { resourceOwnerId });
    },
  });
}
